import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BASE_API_URL, IGrowFilterRequest, ILeadContact, IListing, IUser } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { BehaviorSubject, combineLatest, Observable, Subject, tap } from 'rxjs';

const API_URL = BASE_API_URL + 'grow';

@Injectable({
  providedIn: 'root',
})
export class GrowService {
  private leadsSubject: Subject<{ totalLeads: number; totalUsers: number; leads: ILeadContact[]; users: IUser[] }>;
  leads$: Observable<{ totalLeads: number; totalUsers: number; leads: ILeadContact[]; users: IUser[] }>;
  private prospectsSubject: Subject<{ total: number; results: IListing[] }>;
  prospects$: Observable<{ total: number; results: IListing[] }>;
  private clientsSubject: Subject<{ total: number; results: IUser[] }>;
  clients$: Observable<{ total: number; results: IUser[] }>;
  private querySubject: Subject<string>;
  query$: Observable<string>;

  constructor(private http: HttpClient) {
    this.leadsSubject = new BehaviorSubject<{
      totalLeads: number;
      totalUsers: number;
      leads: ILeadContact[];
      users: IUser[];
    }>({ totalLeads: 0, totalUsers: 0, leads: [], users: [] });
    this.leads$ = this.leadsSubject.asObservable();
    this.prospectsSubject = new BehaviorSubject<{ total: number; results: IListing[] }>({ total: 0, results: [] });
    this.prospects$ = this.prospectsSubject.asObservable();
    this.clientsSubject = new BehaviorSubject<{ total: number; results: IUser[] }>({ total: 0, results: [] });
    this.clients$ = this.clientsSubject.asObservable();
    this.querySubject = new BehaviorSubject('');
    this.query$ = this.querySubject.asObservable();
  }

  getAll(query: IGrowFilterRequest): Observable<{
    leads: { totalLeads: number; totalUsers: number; leads: ILeadContact[]; users: IUser[] };
    prospects: { total: number; results: IListing[] };
    clients: { total: number; results: IUser[] };
  }> {
    this.querySubject.next(query.queryTerm || '');

    return combineLatest({
      leads: this.getLeads(query),
      prospects: this.getProspects(query),
      clients: this.getClients(query),
    });
  }

  getLeads(
    query: IGrowFilterRequest,
  ): Observable<{ totalLeads: number; totalUsers: number; leads: ILeadContact[]; users: IUser[] }> {
    // resetting to prevent button flash on search
    this.leadsSubject.next({ totalLeads: 0, totalUsers: 0, leads: [], users: [] });

    const paramQuery = { ...query };
    // set sort to default if it isn't an allowed sort for leads
    if (
      paramQuery.sort !== 'created' &&
      paramQuery.sort !== 'location' &&
      paramQuery.sort !== 'lastName' &&
      paramQuery.sort !== 'firstName'
    ) {
      paramQuery.sort = 'lastName';
    }

    return this.http
      .get<{
        totalLeads: number;
        totalUsers: number;
        leads: ILeadContact[];
        users: IUser[];
      }>(`${API_URL}/leads?${objectToUrlParams(paramQuery)}`)
      .pipe(tap((leads) => this.leadsSubject.next(leads)));
  }

  getProspects(query: IGrowFilterRequest): Observable<{ total: number; results: IListing[] }> {
    // resetting to prevent button flash on search
    this.prospectsSubject.next({ total: 0, results: [] });

    const paramQuery = { ...query };
    // set sort to default if it isn't an allowed sort for prospects
    if (paramQuery.sort !== 'created' && paramQuery.sort !== 'location' && paramQuery.sort !== 'name') {
      paramQuery.sort = 'created';
    }

    return this.http
      .get<{ total: number; results: IListing[] }>(`${API_URL}/prospects?${objectToUrlParams(paramQuery)}`)
      .pipe(tap((prospects) => this.prospectsSubject.next(prospects)));
  }

  getClients(query: IGrowFilterRequest): Observable<{ total: number; results: IUser[] }> {
    // resetting to prevent button flash on search
    this.clientsSubject.next({ total: 0, results: [] });

    const paramQuery = { ...query };
    // set sort to default if it isn't an allowed sort for clients
    if (
      paramQuery.sort !== 'created' &&
      paramQuery.sort !== 'location' &&
      paramQuery.sort !== 'lastName' &&
      paramQuery.sort !== 'firstName'
    ) {
      paramQuery.sort = 'lastName';
    }

    return this.http
      .get<{ total: number; results: IUser[] }>(`${API_URL}/clients?${objectToUrlParams(paramQuery)}`)
      .pipe(tap((clients) => this.clientsSubject.next(clients)));
  }

  register(user: IUser) {
    return this.http.post<IUser>(`${API_URL}/register-client`, user);
  }

  checkEmailAvailability(email: string) {
    return this.http.get<string>(`${API_URL}/client/email-check?email=${encodeURIComponent(email)}`);
  }

  createLead(lead: ILeadContact): Observable<ILeadContact> {
    return this.http.post<ILeadContact>(`${API_URL}/lead`, lead);
  }

  updateLead(lead: ILeadContact): Observable<ILeadContact> {
    return this.http.put<ILeadContact>(`${API_URL}/lead/${lead.id}`, lead);
  }

  removeLead(id: number): Observable<ILeadContact> {
    return this.http.delete<ILeadContact>(`${API_URL}/lead/${id}`);
  }

  updateUser(user: IUser): Observable<IUser> {
    return this.http.put<IUser>(`${BASE_API_URL}user/${user.id}`, user);
  }

  notifyClientListingReady(id: number) {
    return this.http.get<string>(`${API_URL}/client/email-listing-notify/${id}`);
  }

  sendWelcomeEmail(userId: number) {
    return this.http.post<boolean>(`${API_URL}/${userId}/send-welcome-email`, {});
  }

  // only sales managers can force a send temp password and it will only work if they haven't logged in yet
  sendTemporaryPassword(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${BASE_API_URL}auth/temporary-password`, { email });
  }
}
